import { LogRowsEditResult } from '../../../models/logs/LogRowsEditResult';
import { ReceivingLogDetails } from '../../../models/logs/ReceivingLogDetails';

/**
 * log-row-form.rules.ts
 *
 * The OLD free-text log form — the Entry Details modal's "Update Entry" — reduced
 * to the two decisions that can damage a log, so both are testable away from
 * Angular:
 *
 * 1. {@link isLogFormFieldLocked} — which columns a log that HAS structured rows
 *    must not let anyone type over.
 * 2. {@link diffLogRowFields} — which fields actually go on the request.
 *
 * Both exist because `PUT /V1/update-receiving-log` is the endpoint with no
 * defences. Its own quantity guard fires only on a MIXED-container log and only
 * when the sent value differs from stored, so a single-type structured log —
 * 3 BOX and nothing else — accepts a hand-typed quantity with no complaint, and
 * the string stops describing the container rows that printed the labels. That
 * is not a legacy-only exposure; it is any structured log with one container
 * type.
 */

/**
 * What we know about a log's structured rows.
 *
 * `unknown` is a real third state, not a placeholder: the Entry Details modal is
 * built from the table row already in memory, which carries no such flag, so the
 * answer arrives from `GET /V1/receiving-log/{logId}/details` a moment after the
 * modal opens — and may not arrive at all.
 *
 * It must behave like `structured`, never like `legacy`. `legacy` licenses
 * free-text editing of the two derived strings; a read that failed is not a
 * licence, and the details payload takes the same position (`hasStructuredRows`
 * reports `true`, never `false`, when a sub-fetch soft-fails — handoff §1.1).
 */
export type StructuredRowsState = 'unknown' | 'structured' | 'legacy';

/**
 * The log-form columns a dedicated editor owns on a structured log.
 *
 * BOTH are projections of child rows once a log has them:
 *
 * - `quantity` is composed by the BACKEND from the container rows inside the
 *   save transaction, so a hand-typed value disagrees with the numbers printed
 *   on stickers already stuck to boxes.
 * - `items` is composed by the CLIENT from the receipt rows (the same
 *   `items-string.rules.ts` composition the submit runs), so typing over it
 *   makes the note describe a delivery the receipt ledger does not — and that
 *   ledger is what drives `qtyReceived` and `isClosed` on the PO screen.
 *
 * Each gets its own editor, opened from a button on its own field, because they
 * are edited with completely different controls: containers by chips, items by
 * PO line-item check-off.
 */
export const ROW_DERIVED_LOG_FORM_FIELDS: readonly string[] = ['quantity', 'items'];

/** Which editor owns a locked field — the modal its button opens. */
export type LogFieldEditor = 'quantity' | 'items';

/** The editor that owns a column, or null when the column is plain free text. */
export function editorForField(columnName: string, rows: StructuredRowsState): LogFieldEditor | null {
  if (!isLogFormFieldLocked(columnName, rows)) { return null; }
  return columnName === 'items' ? 'items' : 'quantity';
}

/** Reads the details payload's own claim. Anything short of a confirmed `false` is not `legacy`. */
export function structuredRowsStateFrom(details: ReceivingLogDetails | null | undefined): StructuredRowsState {
  if (!details) { return 'unknown'; }
  return details.hasStructuredRows === false ? 'legacy' : 'structured';
}

/**
 * Whether a column must be read-only on the old free-text form.
 *
 * Not role-gated. An admin typing a quantity onto a structured log breaks the
 * same invariant as anyone else, and the endpoint will accept it just as
 * cheerfully.
 */
export function isLogFormFieldLocked(columnName: string, rows: StructuredRowsState): boolean {
  return rows !== 'legacy' && ROW_DERIVED_LOG_FORM_FIELDS.includes(columnName);
}

/**
 * Why a field is locked, in the user's terms — or null when it is editable.
 *
 * The `unknown` case says the truth ("we could not check") rather than claiming
 * the log has rows. Telling someone their genuinely legacy log is structured is
 * a smaller harm than the reverse, but it is still a false statement.
 */
export function lockedFieldReason(columnName: string, rows: StructuredRowsState): string | null {
  if (!isLogFormFieldLocked(columnName, rows)) { return null; }

  if (rows === 'unknown') {
    const what = columnName === 'items' ? 'items note' : 'quantity';
    return `We could not check whether this log has structured rows, so the ${what} is locked. ` +
      'Close and reopen this entry to try again.';
  }

  // Same shape of sentence for both, because it is the same rule: the string is a
  // projection of child rows, and typing over it makes it contradict them. Only
  // the rows differ — containers for one, receipts for the other.
  return columnName === 'items'
    ? 'This log\'s items note is built from its receipt rows, which are what the PO reads as ' +
      'received. Change it with "Edit items".'
    : 'This log\'s quantity is built from its container rows and matches the labels that printed. ' +
      'Change it with "Edit containers".';
}

/**
 * The changed-fields object the old update sends — the same diff the form has
 * always done, minus the fields nobody may type over.
 *
 * ⚠️ The two things this must get right, both of which are silent when wrong:
 *
 * - **Locked fields are dropped even when they differ.** An Angular reactive form
 *   keeps a `.value` on a DISABLED control, and the form reader takes `.value`
 *   from every control, so rendering an input read-only does NOT by itself keep
 *   it out of the request. The exclusion has to be explicit, here.
 * - **The baseline must be CURRENT.** This diffs against whatever it is given; if
 *   the caller hands it the row as the table loaded it, and the nested row editor
 *   has since committed a save, the difference it finds in `quantity`/`items` is
 *   the SERVER'S change, not the user's — and sending it back overwrites what the
 *   row editor derived. Re-baseline after the nested modal closes.
 *
 * @param formValues The form's current values, keyed by column name.
 * @param baseline The row as it is believed to be stored RIGHT NOW.
 * @param columnNames The columns the form renders, in the order it renders them.
 * @param rows What we know about the log's structured rows.
 * @returns Only the fields that changed and are allowed to change.
 */
export function diffLogRowFields(
  formValues: Record<string, unknown>,
  baseline: Record<string, unknown>,
  columnNames: readonly string[],
  rows: StructuredRowsState,
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};

  for (const name of columnNames ?? []) {
    if (!Object.prototype.hasOwnProperty.call(formValues ?? {}, name)) { continue; }
    if (isLogFormFieldLocked(name, rows)) { continue; }
    if (formValues[name] !== (baseline ?? {})[name]) {
      changed[name] = formValues[name];
    }
  }

  return changed;
}

/**
 * The past-tense record the Entry Details modal shows after the nested row editor
 * saved and closed.
 *
 * Past tense on purpose: this is not a pending badge waiting for "Update Entry"
 * to apply it. The row editor already committed, in its own transaction. The
 * parent's job is to make clear that the entry in front of the user is no longer
 * the one it was when they opened it.
 *
 * @param before The quantity/items as the modal opened with them.
 * @param saved What came back on the row editor's `PUT`.
 */
export function rowEditorChangeSummary(
  before: { quantity?: string | null; items?: string | null },
  saved: LogRowsEditResult,
): string[] {
  const lines: string[] = [];

  const wasQuantity = (before?.quantity ?? '').trim();
  const nowQuantity = (saved?.quantity ?? '').trim();
  if (nowQuantity && nowQuantity !== wasQuantity) {
    lines.push(`Quantity is now ${nowQuantity}.`);
  }

  if ((saved?.items ?? '') !== (before?.items ?? '')) {
    lines.push('The items note was rebuilt from the rows.');
  }

  lines.push(...(saved?.summaryLines ?? []));
  return lines;
}
